import type { Marker, Renderer } from '@googlemaps/markerclusterer';
import type { Theme } from '@mui/material';

import { pluralize } from '@core/utils/pluralize';

import { getSlabMapMarkerClusterMeta } from '../SlabMap/slabMapMarkerClusterMeta';
import type { PinType } from './mapPins';

const LOCATION_TYPE_ORDER: PinType[] = ['plant', 'competitor-plant', 'job-site'];

const LOCATION_TYPE_LABEL: Record<PinType, string> = {
  plant: 'location',
  'competitor-plant': 'competitor location',
  'job-site': 'job site',
};

const CLUSTER_RING_PX = 4;

export type ClusterTypeSegment = {
  type: PinType;
  color: string;
  count: number;
};

export const aggregateClusterTypeSegments = (markers: Marker[]): ClusterTypeSegment[] => {
  const counts = new Map<PinType, { color: string; count: number }>();

  markers.forEach((marker) => {
    const meta = getSlabMapMarkerClusterMeta(marker);
    if (!meta?.locationType) return;

    const type = meta.locationType as PinType;
    const existing = counts.get(type);
    if (existing) {
      existing.count += 1;
    } else {
      counts.set(type, { color: meta.color, count: 1 });
    }
  });

  return LOCATION_TYPE_ORDER.flatMap((type) => {
    const entry = counts.get(type);
    if (!entry) return [];
    return [{ type, color: entry.color, count: entry.count }];
  });
};

export const buildClusterConicGradient = (segments: ClusterTypeSegment[]): string => {
  const total = segments.reduce((sum, { count }) => sum + count, 0);
  if (total === 0) return '';

  let angle = 0;
  const stops = segments.map(({ color, count }) => {
    const sweep = (count / total) * 360;
    const start = angle;
    angle += sweep;
    return `${color} ${start}deg ${angle}deg`;
  });

  return `conic-gradient(${stops.join(', ')})`;
};

export const buildClusterTitle = (segments: ClusterTypeSegment[]): string =>
  segments
    .map(({ type, count }) => `${count} ${pluralize(count, LOCATION_TYPE_LABEL[type])}`)
    .join(', ');

const clusterSizeForCount = (count: number): number =>
  Math.min(56, 36 + Math.round(Math.log2(count + 1) * 6));

const clusterFontSizeForCount = (count: number): number => (count >= 100 ? 12 : 14);

const createClusterElement = (
  count: number,
  segments: ClusterTypeSegment[],
  theme: Theme,
): HTMLDivElement => {
  const size = clusterSizeForCount(count);
  const fontSize = clusterFontSizeForCount(count);
  const innerSize = size - CLUSTER_RING_PX * 2;
  const gradient = buildClusterConicGradient(segments);
  const fallbackFill = theme.palette.SlabIndigo[700];

  const outer = document.createElement('div');
  outer.style.cssText = [
    `width: ${size}px`,
    `height: ${size}px`,
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'border-radius: 50%',
    `background: ${gradient || fallbackFill}`,
    'box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2)',
    'cursor: pointer',
    'box-sizing: border-box',
  ].join(';');

  if (segments.length > 0) {
    outer.title = buildClusterTitle(segments);
  }

  const inner = document.createElement('div');
  inner.style.cssText = [
    `width: ${innerSize}px`,
    `height: ${innerSize}px`,
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'border-radius: 50%',
    `background: ${theme.palette.SlabGray[0]}`,
    `color: ${theme.palette.SlabGray[900]}`,
    `font: 600 ${fontSize}px/1 ${theme.typography.fontFamily ?? 'Inter, sans-serif'}`,
    'box-sizing: border-box',
  ].join(';');
  inner.textContent = String(count);

  outer.appendChild(inner);
  return outer;
};

export const createPinClusterRenderer = (theme: Theme): Renderer => ({
  render: ({ count, position, markers }): google.maps.marker.AdvancedMarkerElement => {
    const segments = aggregateClusterTypeSegments(markers);

    return new google.maps.marker.AdvancedMarkerElement({
      position,
      content: createClusterElement(count, segments, theme),
      zIndex: 1000 + count,
    });
  },
});
